/**
 * Sync Helpers - DRY utilities for syncedSupabase create/update operations
 * 
 * These factory functions create custom create/update handlers that:
 * 1. Handle "0 rows updated" edge case (upsert fallback)
 * 2. Handle foreign key errors (auto-fix by clearing the FK)
 * 3. Handle duplicate key conflicts (upsert fallback)
 */

import { supabase } from '../lib/supabaseClient';

const SYNC_ERROR_TYPES = {
  FOREIGN_KEY: '23503',
  DUPLICATE_KEY: '23505',
};

/**
 * Creates a custom create function for syncedSupabase
 * @param {string} tableName - Supabase table name
 * @param {object} options - Options for error handling
 * @param {function} options.onForeignKeyError - Callback to fix data on foreign key error
 * @param {string} options.onConflict - Column(s) for upsert conflict resolution (default: 'id')
 */
export const createSyncCreate = (tableName, options = {}) => {
  const { onForeignKeyError, onConflict = 'id' } = options;

  return async (input) => {
    const { data, error } = await supabase
      .from(tableName)
      .insert(input)
      .select()
      .single();

    if (error) {
      const errorCode = error.code;

      // 23503 - Foreign key violation
      if (errorCode === SYNC_ERROR_TYPES.FOREIGN_KEY && onForeignKeyError) {
        console.warn(`[Sync] Foreign key error for ${tableName}, attempting fix...`);
        const fixedInput = onForeignKeyError(input);
        const { data: retryData, error: retryError } = await supabase
          .from(tableName)
          .insert(fixedInput)
          .select()
          .single();

        if (retryError) {
          throw new Error(retryError.message);
        }
        return retryData;
      }

      // 23505 - Duplicate key violation (conflict) - use upsert
      if (errorCode === SYNC_ERROR_TYPES.DUPLICATE_KEY) {
        console.warn(`[Sync] Duplicate key for ${tableName}, using upsert...`);
        const { data: upsertData, error: upsertError } = await supabase
          .from(tableName)
          .upsert(input, { onConflict })
          .select()
          .single();

        if (upsertError) {
          throw new Error(upsertError.message);
        }
        return upsertData;
      }

      throw new Error(error.message);
    }

    return data;
  };
};

/**
 * Creates a custom update function for syncedSupabase
 * Handles the "0 rows updated" edge case by falling back to upsert
 *
 * IMPORTANT: Always ensures updated_at is set, especially for delete operations.
 * Without this, incremental sync (changesSince: 'last-sync') won't see the change.
 *
 * @param {string} tableName - Supabase table name
 * @param {object} options - Options for error handling
 * @param {function} options.onForeignKeyError - Callback to fix data on foreign key error
 * @param {string} options.onConflict - Column(s) for upsert conflict resolution (default: 'id')
 */
export const createSyncUpdate = (tableName, options = {}) => {
  const { onForeignKeyError, onConflict = 'id' } = options;

  return async (input) => {
    // Ensure updated_at is set
    const dataWithTimestamp = {
      ...input,
      updated_at: input.updated_at || new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from(tableName)
      .update(dataWithTimestamp)
      .eq('id', input.id)
      .select();

    if (error) {
      const errorCode = error.code;

      // 23503 - Foreign key violation
      if (errorCode === SYNC_ERROR_TYPES.FOREIGN_KEY && onForeignKeyError) {
        console.warn(`[Sync] Foreign key error for ${tableName}, attempting fix...`);
        const fixedInput = onForeignKeyError(dataWithTimestamp);
        const { data: retryData, error: retryError } = await supabase
          .from(tableName)
          .update(fixedInput)
          .eq('id', fixedInput.id)
          .select();

        if (retryError) {
          throw new Error(retryError.message);
        }
        return retryData?.[0] || null;
      }

      // 23505 - Duplicate key violation (conflict) - use upsert
      if (errorCode === SYNC_ERROR_TYPES.DUPLICATE_KEY) {
        console.warn(`[Sync] Duplicate key for ${tableName}, using upsert...`);
        const { data: upsertData, error: upsertError } = await supabase
          .from(tableName)
          .upsert(dataWithTimestamp, { onConflict })
          .select();

        if (upsertError) {
          throw new Error(upsertError.message);
        }
        return upsertData?.[0] || null;
      }

      throw new Error(error.message);
    }

    // Handle "0 rows updated" edge case - row doesn't exist, use upsert
    if (!data || data.length === 0) {
      console.warn(`[Sync] No rows updated for ${tableName}, using upsert...`);
      const { data: upsertData, error: upsertError } = await supabase
        .from(tableName)
        .upsert(dataWithTimestamp, { onConflict })
        .select();

      if (upsertError) {
        throw new Error(upsertError.message);
      }
      return upsertData?.[0] || null;
    }

    return data?.[0] || null;
  };
};

/**
 * Default syncedSupabase configuration for stores
 *
 * We now use a dedicated boolean `deleted` column with `changesSince: 'last-sync'`
 * for efficient incremental sync. This relies on always bumping `updated_at`
 * in createSyncUpdate so soft deletes are visible to incremental sync.
 */
export const defaultSyncConfig = {
  realtime: true,
  fieldCreatedAt: 'created_at',
  fieldUpdatedAt: 'updated_at',
  fieldDeleted: 'deleted',
  changesSince: 'last-sync',
  retry: {
    times: 3,
    backoff: 'exponential',
    maxDelay: 30000,
  },
};

